import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';
import SiteInformationTab from './SiteInformationTab';
import SiteDesignTab from './SiteDesignTab';
import SiteMetricsTab from './SiteMetricsTab';
import SitePromotionsTab from './SitePromotionsTab';
import './AdminAccount.css';

const OnlineOrderingPage = () => {
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(null);
  const [publicStore, setPublicStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('information');
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await initPage(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initPage = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();

      const userDoc = await getDoc(doc(db, 'users', uid));
      let selectedLocationId = null;
      if (userDoc.exists()) {
        selectedLocationId = userDoc.data().selectedLocationId;
      }

      if (!selectedLocationId) {
        Swal.fire('Error', 'Please select a location first in Locations page.', 'error');
        setLoading(false);
        return;
      }

      const locDoc = await getDoc(doc(db, 'users', uid, 'locations', selectedLocationId));
      if (!locDoc.exists()) {
        Swal.fire('Error', 'Selected location not found.', 'error');
        setLoading(false);
        return;
      }

      const locData = { id: locDoc.id, ...locDoc.data() };
      setLocation(locData);

      const publicStoreDoc = await getDoc(doc(db, 'publicStores', selectedLocationId));
      if (publicStoreDoc.exists()) {
        setPublicStore({ id: publicStoreDoc.id, ...publicStoreDoc.data() });
      }
    } catch (error) {
      console.error('Error initializing online ordering page:', error);
      Swal.fire('Error', 'Failed to load page data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Online Ordering">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
          <div className="spinner-border text-orange" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!location) {
    return (
      <AdminLayout title="Online Ordering">
        <div className="alert alert-warning">
          Please select a location first in the <a href="/admin/locations">Locations</a> page.
        </div>
      </AdminLayout>
    );
  }

  const tabButtonClass = (tab) =>
    `btn btn-${activeTab === tab ? 'primary' : 'outline-secondary'} mr-2 mb-2`;

  return (
    <AdminLayout title="Online Ordering">
      <SetupGuard prerequisites={['location', 'floorplan', 'workzones', 'product']}>
      <div className="container-fluid mb-4">
        <div className="d-flex flex-wrap">
          <button className={tabButtonClass('information')} onClick={() => setActiveTab('information')}>
            <i className="fas fa-info-circle mr-2"></i>Site Information
          </button>
          <button className={tabButtonClass('design')} onClick={() => setActiveTab('design')}>
            <i className="fas fa-paint-brush mr-2"></i>Site Design
          </button>
          <button className={tabButtonClass('metrics')} onClick={() => setActiveTab('metrics')}>
            <i className="fas fa-chart-line mr-2"></i>Site Engagement & Metrics
          </button>
          <button className={tabButtonClass('promotions')} onClick={() => setActiveTab('promotions')}>
            <i className="fas fa-tags mr-2"></i>Promotions
          </button>
        </div>
      </div>

      {activeTab === 'information' && (
        <SiteInformationTab user={user} location={location} publicStore={publicStore} setPublicStore={setPublicStore} />
      )}
      {activeTab === 'design' && (
        <SiteDesignTab user={user} location={location} publicStore={publicStore} setPublicStore={setPublicStore} />
      )}
      {activeTab === 'metrics' && (
        <SiteMetricsTab user={user} location={location} publicStore={publicStore} />
      )}
      {activeTab === 'promotions' && (
        <SitePromotionsTab user={user} location={location} publicStore={publicStore} />
      )}
      </SetupGuard>
    </AdminLayout>
  );
};

export default OnlineOrderingPage;
